// RoleSelection.jsx — Redesigned (Glass UI / Copyright-Safe)

import React, { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "../header";
import Footer from "../footer";
import { APPNAME, AuthContext } from "../util/api";
import { useNavigate } from "react-router-dom";

export default function RoleSelection({ onSelect }) {
  const router = useNavigate();
  const { token, User } = useContext(AuthContext);

  const [role, setRole] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);

  const isMobile = width < 768;

  useEffect(() => {
    if (!token || !User) {
      router("/login");
    } else if (User.role !== "none") {
      if (User.role === "doctor") router("/lawyer-dashboard");
      if (User.role === "patient") router("/client-dashboard");
    }

    const resize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);



  const roles = [
  {
    id: "doctor",
    title: "Lawyer",
    desc: "Manage cases, clients & legal consultations",
    gradient: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
    icon: "⚖️",
  },
  {
    id: "patient",
    title: "Client",
    desc: "Find lawyers, book consultations & track cases",
    gradient: "linear-gradient(135deg, #7c2d12, #f97316)",
    icon: "🧑‍💼",
  },
];

  const confirm = () => {
    if (!role) return;
    if (onSelect) onSelect(role);
    if (role === "doctor") router("/lawyer-profile-reg");
    if (role === "patient") router("/client-dashboard");
  };

  return (
    <>
      <Navbar menuColor={{ color: "#020617" }} />

      <style>{`
        .glass {
          backdrop-filter: blur(18px);
          background: rgba(255,255,255,0.7);
          border: 1px solid rgba(255,255,255,0.4);
        }
      `}</style>

      <main
        style={{
          minHeight: "100vh",
          paddingTop: "120px",
          paddingBottom: "80px",
          background:
            "radial-gradient(circle at top, #e0f2fe, #f8fafc 45%, #eef2ff)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "1050px",
            padding: isMobile ? "20px" : "40px",
          }}
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{
              textAlign: "center",
              marginBottom: isMobile ? "35px" : "55px",
            }}
          >
            <h1
              style={{
                fontSize: isMobile ? "30px" : "46px",
                fontWeight: 900,
                color: "#020617",
              }}
            >
              Welcome to {APPNAME}
            </h1>

            <p
              style={{
                marginTop: "10px",
                color: "#475569",
                fontSize: isMobile ? "15px" : "18px",
              }}
            >
              Select how you would like to use the platform
            </p>
          </motion.div>

          {/* Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "28px",
            }}
          >
            {roles.map((r) => (
              <motion.div
                key={r.id}
                whileHover={{ y: -8 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setRole(r.id)}
                className="glass"
                style={{
                  cursor: "pointer",
                  borderRadius: "26px",
                  padding: isMobile ? "26px" : "38px",
                  boxShadow:
                    role === r.id
                      ? "0 25px 55px rgba(0,0,0,0.25)"
                      : "0 15px 40px rgba(0,0,0,0.12)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: r.gradient,
                    opacity: role === r.id ? 0.2 : 0.08,
                  }}
                />

                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      fontSize: "42px",
                      marginBottom: "15px",
                    }}
                  >
                    {r.icon}
                  </div>

                  <h3
                    style={{
                      fontSize: "26px",
                      fontWeight: 800,
                      color: "#020617",
                    }}
                  >
                    {r.title}
                  </h3>

                  <p
                    style={{
                      marginTop: "8px",
                      color: "#475569",
                      fontSize: "15px",
                      maxWidth: "320px",
                    }}
                  >
                    {r.desc}
                  </p>

                  {role === r.id && (
                    <div
                      style={{
                        marginTop: "18px",
                        fontWeight: 600,
                        color: "#16a34a",
                      }}
                    >
                      ✔ Selected
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "15px",
              marginTop: "45px",
            }}
          >
            <button
              onClick={() => setRole(null)}
              style={{
                padding: "10px 22px",
                borderRadius: "12px",
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#fd2c08ff",
                cursor: "pointer",
              }}
            >
              Reset
            </button>

            <button
              disabled={!role}
              onClick={confirm}
              style={{
                padding: "12px 28px",
                borderRadius: "14px",
                background: role
                  ? "linear-gradient(to right, #6366f1, #8b5cf6)"
                  : "#cbd5e1",
                color: "#fff",
                fontWeight: 700,
                cursor: role ? "pointer" : "not-allowed",
                boxShadow: role
                  ? "0 18px 40px rgba(99,102,241,0.4)"
                  : "none",
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
